import React, { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { THEMES } from "../../../constants";
import Logo from "../../../components/Logo";
import Account from "./TopBar/Account";
import Notifications from "./TopBar/Notifications";
import ShoppingCart from "./TopBar/ShoppingCart";
import Search from "./TopBar/Search";
import HelpCenter from "./TopBar/HelpCenter";
import HeaderTitle from "./TopBar/HeaderTitle";
import useAuth from "../../../hooks/useAuth";
import SidebarContent from "./SidebarContent";

const drawerWidth = 180;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  border: "none",
  backgroundColor: "transparent",
  boxShadow: "none",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  borderRight: "none",
  backgroundColor: "transparent",
  boxShadow: "none",
});

const styles = {
  logo: { maxHeight: 40, maxWidth: "100%" },
};

export default function MiniDrawer() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(localStorage.getItem("openedDrawer") == "true");
  const [isHovered, setIsHovered] = React.useState(false);
  const { user } = useAuth();

  const isDrawerOpenForLayout = open || isHovered;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleDesktopDrawerMouseEnter = () => {
    if (!open) {
      setIsHovered(true);
    }
  };

  const handleDesktopDrawerMouseLeave = () => {
    if (!open) {
      setIsHovered(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("openedDrawer", open);
  }, [open]);

  const content = <SidebarContent open={isDrawerOpenForLayout} />;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: { xs: theme.zIndex.appBar, lg: theme.zIndex.drawer + 1 },
          backgroundColor: "transparent",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          [theme.breakpoints.up("lg")]: {
            width: `calc(100% - ${theme.spacing(8)})`,
          },
          boxShadow: "none",
          ...(theme.name === THEMES.LIGHT && {
            backgroundColor: "primary.main",
          }),
          ...(theme.name === THEMES.ONE_DARK && {
            backgroundColor: "background.default",
          }),
          ...(isDrawerOpenForLayout && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            [theme.breakpoints.up("lg")]: {
              width: `calc(100% - ${drawerWidth}px)`,
            },
            transition: theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(0, 1),
            boxShadow: "none",
            ...(theme.name === THEMES.LIGHT && {
              backgroundColor: "primary.main",
            }),
            ...(theme.name === THEMES.ONE_DARK && {
              backgroundColor: "background.default",
            }),
          }}
        >
          <IconButton
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 1,
              ...(isDrawerOpenForLayout && { display: "none" }),
              [theme.breakpoints.up("lg")]: {
                display: "none",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            aria-label="close drawer"
            onClick={handleDrawerClose}
            edge="start"
            sx={{
              marginRight: 1,
              display: open ? "inline-flex" : "none",
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
            <HeaderTitle />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <HelpCenter />
            <Search />
            <Notifications />
            <ShoppingCart />
            <Account />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      <Drawer
        anchor="left"
        onClose={handleDrawerClose}
        open={open}
        variant="temporary"
        disableEnforceFocus
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            whiteSpace: "nowrap",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(0, 1),
            ...theme.mixins.toolbar,
            ...(theme.name === THEMES.LIGHT && {
              backgroundColor: "primary.main",
            }),
            ...(theme.name === THEMES.ONE_DARK && {
              backgroundColor: "background.default",
            }),
          }}
        >
          <Logo
            src={user.company.labelsLogoUrl || undefined}
            style={user.company.labelsLogoUrl ? styles.logo : undefined}
          />
          <IconButton
            color="default"
            aria-label="close drawer"
            onClick={handleDrawerClose}
            edge="start"
            sx={{
              color: "background.default",
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        {content}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="permanent"
        onMouseEnter={handleDesktopDrawerMouseEnter}
        onMouseLeave={handleDesktopDrawerMouseLeave}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          ...(isDrawerOpenForLayout && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": openedMixin(theme),
          }),
          ...(!isDrawerOpenForLayout && {
            ...closedMixin(theme),
            "& .MuiDrawer-paper": closedMixin(theme),
          }),
          display: { xs: "none", lg: "block" },
        }}
        open={isDrawerOpenForLayout}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(0, 1),
            ...theme.mixins.toolbar,
          }}
        >
          <Logo
            src={user.company.labelsLogoUrl || undefined}
            style={user.company.labelsLogoUrl ? styles.logo : undefined}
          />
        </Box>
        {content}
      </Drawer>
    </>
  );
}
